/**
 * @component ry-code
 * @category  display
 * @summary   Syntax-highlighted code block (ry-ui-only). Lifts inner text content (or HTML inside a `<template>`) into a `<pre><code>` with built-in highlighter, optional title bar, line numbers, and a copy-to-clipboard button. Highlighter is in-tree (no Prism/Shiki dependency) and supports css / js / html / json / text.
 *
 * @attribute language      'css' | 'js' | 'javascript' | 'html' | 'json' | 'text' ?   default="text"   Syntax dialect. `text` means no highlighting. Unknown values fall back to text.
 * @attribute title         string?              Header bar label. Defaults to the language uppercase (e.g. "CSS"). Set to suppress the language echo and label the file (e.g. `"styles.css"`).
 * @attribute line-numbers  boolean   default=false   Render a gutter with 1-based line numbers next to each row.
 *
 * @property  code   string   Read/write. Setting replaces the inner content + re-renders + re-highlights.
 *
 * @event ry:beforecopy
 *        detail: { code: string }
 *        fires:  on copy-button click, before `navigator.clipboard.writeText()` is called. `detail.code` is the unhighlighted source about to be written.
 *        cancelable: preventDefault() skips the clipboard write and the visual feedback; neither `ry:copy` nor `ry:copy-error` fires
 *
 * @event ry:copy
 *        detail: { code: string }
 *        fires:  on successful `navigator.clipboard.writeText()`. `detail.code` is the unhighlighted source.
 *
 * @event ry:copy-error
 *        detail: {}
 *        fires:  when `navigator.clipboard.writeText()` rejects (clipboard unavailable, permission denied, insecure context).
 *
 * @content   Authored child code — supports two modes:
 *   1. Plain text inside the element — used directly as the source.
 *   2. `<template>` child — `template.innerHTML` is used as the source. Use this for code that contains HTML tags (`<div>` etc.) that would otherwise be parsed as DOM. The wrapper also trims common indentation.
 *
 * @dom
 *   ry-code[language][title?][line-numbers?]
 *     > div.ry-code__header    [data-ry-target="header"]
 *       > span.ry-code__title  [data-ry-target="title"]   {title or language uppercase}
 *       > button.ry-code__copy [data-ry-target="copy"]   type="button"   aria-label="Copy code"
 *         > {copy icon, swaps to check icon on success for ~2s}
 *     > div.ry-code__content   [data-ry-target="content"]
 *       > pre.ry-code__pre     [data-ry-target="pre"]
 *         > code.ry-code__code [data-ry-target="code"]
 *           > {highlighted spans, or per-line wrappers when [line-numbers]}
 *
 * @gotcha  Use `<template>` for code that contains HTML tags. Without it, `<div>` inside the element would be parsed as actual DOM and never reach the highlighter. The `<template>` content is parsed but not rendered, giving you the raw text to highlight.
 *
 * @gotcha  The highlighter is intentionally minimal — supports css/js/html/json/text only. For full PrismJS / Shiki / Monaco-level grammar, render outside ry-code and pass the highlighted HTML in via a `<template>`. ry-code preserves the `<template>` innerHTML verbatim (no re-highlight).
 *
 * @gotcha  The copy button uses `navigator.clipboard.writeText` which REQUIRES a secure context (https://) and document focus. In iframes, file:// URLs, or non-focused windows the clipboard write rejects and `ry:copy-error` fires instead. happy-dom shims it.
 *
 * @gotcha  Successful copy temporarily swaps the copy icon to a check icon and applies `--success` class for 2 seconds. There's no way to disable this — the visual feedback is intentional. Consumers wanting their own feedback should listen to `ry:copy` and override CSS.
 *
 * @gotcha  Live attribute updates are NOT supported despite `observedAttributes` being declared — the file declares observedAttributes but has no attributeChangedCallback. To change language/title/line-numbers after mount, set the attribute then call `el.code = el.code` to force a re-render.
 *
 * @example basic-css
 * ```html
 * <ry-code language="css" title="styles.css">
 * .button {
 *   background: var(--accent);
 *   border-radius: 8px;
 * }
 * </ry-code>
 * ```
 *
 * @example js-with-line-numbers
 * ```html
 * <ry-code language="js" title="hello.js" line-numbers>
 * function greet(name) {
 *   return `Hello, ${name}!`;
 * }
 * </ry-code>
 * ```
 *
 * @example html-via-template
 * ```html
 * <ry-code language="html" title="demo.html">
 *   <template>
 *     <div class="card">
 *       <h2>Hello</h2>
 *     </div>
 *   </template>
 * </ry-code>
 * ```
 *
 * @example plain-text
 * ```html
 * <ry-code language="text">
 * No syntax highlighting — just monospaced output.
 * </ry-code>
 * ```
 *
 * @tutorial mount-builds-header-and-content
 * ```js
 * const el = mount('<ry-code language="js">const x = 1;</ry-code>');
 * await expectEqual(!!el.querySelector('[data-ry-target="header"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="content"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="pre"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="code"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="copy"]'), true);
 * ```
 *
 * @tutorial title-defaults-to-language-uppercase
 * ```js
 * const el = mount('<ry-code language="js">x</ry-code>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]').textContent, 'JS');
 * ```
 *
 * @tutorial explicit-title-wins
 * ```js
 * const el = mount('<ry-code language="js" title="example.js">x</ry-code>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]').textContent, 'example.js');
 * ```
 *
 * @tutorial line-numbers-render-gutter
 * ```js
 * const el = mount('<ry-code language="js" line-numbers>a\nb\nc</ry-code>');
 * const lines = el.querySelectorAll('.ry-code__line');
 * await expectEqual(lines.length, 3);
 * const nums = el.querySelectorAll('.ry-code__line-number');
 * await expectEqual(nums.length, 3);
 * await expectEqual(nums[0].textContent, '1');
 * await expectEqual(nums[1].textContent, '2');
 * await expectEqual(nums[2].textContent, '3');
 * ```
 *
 * @tutorial no-line-numbers-by-default
 * ```js
 * const el = mount('<ry-code language="js">a\nb</ry-code>');
 * await expectEqual(el.querySelector('.ry-code__line'), null, 'no per-line wrappers without [line-numbers]');
 * ```
 *
 * @tutorial template-child-preserves-html-tags
 * ```js
 * // Wrong assumption: HTML tags inside <ry-code> can be authored as plain
 * // children. They CAN'T — the parser eats them. Use a <template>.
 * const el = mount(`
 *   <ry-code language="html">
 *     <template><div class="card"><h2>Hi</h2></div></template>
 *   </ry-code>
 * `);
 * const code = el.querySelector('[data-ry-target="code"]');
 * // The highlighted output should mention "div" + "class" + "card" + "Hi"
 * await expectEqual(code.textContent.includes('div'), true);
 * await expectEqual(code.textContent.includes('card'), true);
 * await expectEqual(code.textContent.includes('Hi'), true);
 * ```
 *
 * @tutorial copy-button-fires-ry-copy
 * ```js
 * const el = mount('<ry-code language="text">hello world</ry-code>');
 * const copyBtn = el.querySelector('[data-ry-target="copy"]');
 * const fired = [];
 * el.addEventListener('ry:copy', (e) => fired.push(e.detail.code));
 * el.addEventListener('ry:copy-error', () => fired.push('error'));
 * copyBtn.click();
 * // Wait for async clipboard write
 * await sleep(40);
 * // Either success OR error fired (depends on clipboard availability)
 * await expectEqual(fired.length, 1, 'one event fired');
 * ```
 *
 * @tutorial beforecopy-veto
 * ```js
 * // preventDefault() on ry:beforecopy skips the clipboard write entirely —
 * // no ry:copy, no ry:copy-error, no success flash on the button.
 * const el = mount('<ry-code language="text">hello world</ry-code>');
 * const copyBtn = el.querySelector('[data-ry-target="copy"]');
 * const fired = [];
 * el.addEventListener('ry:copy', () => fired.push('copy'));
 * el.addEventListener('ry:copy-error', () => fired.push('error'));
 * el.addEventListener('ry:beforecopy', (e) => e.preventDefault(), { once: true });
 * copyBtn.click();
 * await sleep(40);
 * await expectEqual(fired.length, 0, 'vetoed copy fires neither ry:copy nor ry:copy-error');
 * await expectEqual(copyBtn.classList.contains('ry-code__copy--success'), false, 'no success flash');
 * copyBtn.click();
 * await sleep(40);
 * await expectEqual(fired.length, 1, 'without the veto, the copy proceeds');
 * ```
 *
 * @tutorial code-property-replaces-content
 * ```js
 * const el = mount('<ry-code language="js">old</ry-code>');
 * el.code = 'const next = 2;';
 * const code = el.querySelector('[data-ry-target="code"]');
 * await expectEqual(code.textContent.includes('next'), true, 'new content rendered');
 * await expectEqual(code.textContent.includes('old'), false, 'old content gone');
 * ```
 *
 * @see ry-example
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

type Language = 'css' | 'js' | 'javascript' | 'html' | 'json' | 'text';

interface Token {
  type: 'keyword' | 'property' | 'value' | 'string' | 'number' | 'comment' | 'selector' | 'punctuation' | 'tag' | 'attribute' | 'text' | 'color';
  value: string;
  color?: string;
}

export class RyCode extends RyElement {
  #originalContent = '';

  static get observedAttributes(): string[] {
    return ['language', 'title', 'line-numbers'];
  }

  setup(): void {
    // Check for template first (preserves raw HTML content)
    const template = this.querySelector('template');
    if (template) {
      this.#originalContent = this.#trimIndent(template.innerHTML);
    } else {
      // Fall back to textContent (works for non-HTML code)
      this.#originalContent = this.textContent?.trim() ?? '';
    }

    // Build the component structure
    this.#render();

    // Add copy button handler
    const copyBtn = this.$<HTMLButtonElement>('[data-ry-target="copy"]');
    if (copyBtn) {
      this.on(copyBtn, 'click', this.#handleCopy);
    }
  }

  #render(): void {
    const language = this.getAttribute('language') ?? 'text';
    const title = this.getAttribute('title') ?? language.toUpperCase();
    const showLineNumbers = this.hasAttribute('line-numbers');

    const highlighted = this.#highlight(this.#originalContent, language as Language);
    const lines = highlighted.split('\n');

    this.innerHTML = `
      <div data-ry-target="header" class="ry-code__header">
        <span data-ry-target="title" class="ry-code__title">${title}</span>
        <button data-ry-target="copy" class="ry-code__copy" type="button" aria-label="Copy code">
          ${getIcon('copy')}
        </button>
      </div>
      <div data-ry-target="content" class="ry-code__content">
        <pre data-ry-target="pre" class="ry-code__pre"><code data-ry-target="code" class="ry-code__code">${
          showLineNumbers
            ? lines.map((line, i) => `<span class="ry-code__line"><span class="ry-code__line-number">${i + 1}</span><span class="ry-code__line-content">${line || ' '}</span></span>`).join('')
            : highlighted
        }</code></pre>
      </div>
    `;
  }

  #handleCopy = async (): Promise<void> => {
    const copyBtn = this.$<HTMLButtonElement>('[data-ry-target="copy"]');
    if (!copyBtn) return;
    if (!this.emit('beforecopy', { code: this.#originalContent })) return;

    try {
      await navigator.clipboard.writeText(this.#originalContent);

      // Visual feedback
      copyBtn.innerHTML = getIcon('check');
      copyBtn.classList.add('ry-code__copy--success');

      this.emit('copy', { code: this.#originalContent });

      setTimeout(() => {
        copyBtn.innerHTML = getIcon('copy');
        copyBtn.classList.remove('ry-code__copy--success');
      }, 2000);
    } catch {
      this.emit('copy-error');
    }
  };

  #highlight(code: string, language: Language): string {
    switch (language) {
      case 'css':
        return this.#highlightCSS(code);
      case 'js':
      case 'javascript':
        return this.#highlightJS(code);
      case 'html':
        return this.#highlightHTML(code);
      case 'json':
        return this.#highlightJSON(code);
      default:
        return this.#escape(code);
    }
  }

  #highlightCSS(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Comments
      if (remaining.startsWith('/*')) {
        const end = remaining.indexOf('*/');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 2) });
          remaining = remaining.slice(end + 2);
          continue;
        }
      }

      // Selectors (before {)
      const selectorMatch = remaining.match(/^([.#]?[\w-]+(?:\s*,\s*[.#]?[\w-]+)*)\s*(?=\{)/);
      if (selectorMatch?.[1]) {
        tokens.push({ type: 'selector', value: selectorMatch[1] });
        remaining = remaining.slice(selectorMatch[0].length);
        continue;
      }

      // Properties
      const propMatch = remaining.match(/^([\w-]+)\s*:/);
      if (propMatch?.[1]) {
        tokens.push({ type: 'property', value: propMatch[1] });
        tokens.push({ type: 'punctuation', value: ':' });
        remaining = remaining.slice(propMatch[0].length);
        continue;
      }

      // Color values (hex)
      const hexMatch = remaining.match(/^(#[0-9a-fA-F]{3,8})/);
      if (hexMatch?.[1]) {
        tokens.push({ type: 'color', value: hexMatch[1], color: hexMatch[1] });
        remaining = remaining.slice(hexMatch[1].length);
        continue;
      }

      // Color values (rgb/rgba/hsl/hsla)
      const colorFnMatch = remaining.match(/^(rgba?\([^)]+\)|hsla?\([^)]+\))/i);
      if (colorFnMatch?.[1]) {
        tokens.push({ type: 'color', value: colorFnMatch[1], color: colorFnMatch[1] });
        remaining = remaining.slice(colorFnMatch[1].length);
        continue;
      }

      // Numbers with units
      const numMatch = remaining.match(/^(-?[\d.]+(?:px|em|rem|%|vh|vw|deg|s|ms)?)/);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Strings
      const strMatch = remaining.match(/^(['"])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{};,()]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace or other
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Keywords/values
      const wordMatch = remaining.match(/^([\w-]+)/);
      if (wordMatch?.[1]) {
        const word = wordMatch[1];
        const cssKeywords = ['inherit', 'initial', 'unset', 'none', 'auto', 'transparent', 'solid', 'dashed', 'dotted', 'flex', 'grid', 'block', 'inline', 'inline-block', 'absolute', 'relative', 'fixed', 'sticky'];
        if (cssKeywords.includes(word)) {
          tokens.push({ type: 'keyword', value: word });
        } else {
          tokens.push({ type: 'value', value: word });
        }
        remaining = remaining.slice(word.length);
        continue;
      }

      // Single character fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightJS(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    const jsKeywords = ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'new', 'class', 'extends', 'import', 'export', 'default', 'from', 'async', 'await', 'typeof', 'instanceof', 'in', 'of', 'true', 'false', 'null', 'undefined', 'this', 'super', 'static', 'get', 'set'];

    while (remaining.length > 0) {
      // Single-line comments
      if (remaining.startsWith('//')) {
        const end = remaining.indexOf('\n');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end) });
          remaining = remaining.slice(end);
          continue;
        } else {
          tokens.push({ type: 'comment', value: remaining });
          break;
        }
      }

      // Multi-line comments
      if (remaining.startsWith('/*')) {
        const end = remaining.indexOf('*/');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 2) });
          remaining = remaining.slice(end + 2);
          continue;
        }
      }

      // Strings
      const strMatch = remaining.match(/^(['"`])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Numbers
      const numMatch = remaining.match(/^(-?\d+\.?\d*(?:e[+-]?\d+)?)/i);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Words
      const wordMatch = remaining.match(/^([a-zA-Z_$][\w$]*)/);
      if (wordMatch?.[1]) {
        const word = wordMatch[1];
        if (jsKeywords.includes(word)) {
          tokens.push({ type: 'keyword', value: word });
        } else {
          tokens.push({ type: 'text', value: word });
        }
        remaining = remaining.slice(word.length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{}[\]();,.:?!<>=+\-*/&|^%~@#]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightHTML(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Comments
      if (remaining.startsWith('<!--')) {
        const end = remaining.indexOf('-->');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 3) });
          remaining = remaining.slice(end + 3);
          continue;
        }
      }

      // Tags
      const tagMatch = remaining.match(/^(<\/?)([\w-]+)/);
      if (tagMatch?.[1] && tagMatch[2]) {
        tokens.push({ type: 'punctuation', value: tagMatch[1] });
        tokens.push({ type: 'tag', value: tagMatch[2] });
        remaining = remaining.slice(tagMatch[0].length);
        continue;
      }

      // Attributes
      const attrMatch = remaining.match(/^([\w-]+)(=)/);
      if (attrMatch?.[1]) {
        tokens.push({ type: 'attribute', value: attrMatch[1] });
        tokens.push({ type: 'punctuation', value: '=' });
        remaining = remaining.slice(attrMatch[0].length);
        continue;
      }

      // Strings
      const strMatch = remaining.match(/^(['"])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Closing >
      if (remaining[0] === '>' || remaining.startsWith('/>')) {
        const punct = remaining.startsWith('/>') ? '/>' : '>';
        tokens.push({ type: 'punctuation', value: punct });
        remaining = remaining.slice(punct.length);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Text content
      const textMatch = remaining.match(/^([^<]+)/);
      if (textMatch?.[1]) {
        tokens.push({ type: 'text', value: textMatch[1] });
        remaining = remaining.slice(textMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightJSON(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Strings (keys and values)
      const strMatch = remaining.match(/^("(?:[^\\"]|\\.)*")/);
      if (strMatch?.[1]) {
        // Check if it's a key (followed by :)
        const afterStr = remaining.slice(strMatch[0].length).trimStart();
        if (afterStr.startsWith(':')) {
          tokens.push({ type: 'property', value: strMatch[1] });
        } else {
          tokens.push({ type: 'string', value: strMatch[1] });
        }
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Numbers
      const numMatch = remaining.match(/^(-?\d+\.?\d*(?:e[+-]?\d+)?)/i);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Booleans and null
      const boolMatch = remaining.match(/^(true|false|null)/);
      if (boolMatch?.[1]) {
        tokens.push({ type: 'keyword', value: boolMatch[1] });
        remaining = remaining.slice(boolMatch[1].length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{}[\]:,]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #tokenToHTML(token: Token): string {
    const escaped = this.#escape(token.value);

    switch (token.type) {
      case 'keyword':
        return `<span class="ry-code__keyword">${escaped}</span>`;
      case 'property':
        return `<span class="ry-code__property">${escaped}</span>`;
      case 'value':
        return `<span class="ry-code__value">${escaped}</span>`;
      case 'string':
        return `<span class="ry-code__string">${escaped}</span>`;
      case 'number':
        return `<span class="ry-code__number">${escaped}</span>`;
      case 'comment':
        return `<span class="ry-code__comment">${escaped}</span>`;
      case 'selector':
        return `<span class="ry-code__selector">${escaped}</span>`;
      case 'punctuation':
        return `<span class="ry-code__punctuation">${escaped}</span>`;
      case 'tag':
        return `<span class="ry-code__tag">${escaped}</span>`;
      case 'attribute':
        return `<span class="ry-code__attribute">${escaped}</span>`;
      case 'color':
        return `<span class="ry-code__color"><span class="ry-code__color-preview" style="background-color: ${token.color}"></span>${escaped}</span>`;
      default:
        return escaped;
    }
  }

  #escape(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  #trimIndent(html: string): string {
    const lines = html.split('\n');

    // Remove empty first/last lines
    while (lines.length && !lines[0]?.trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1]?.trim()) lines.pop();

    if (lines.length === 0) return '';

    // Find minimum indentation (ignoring empty lines)
    const minIndent = Math.min(
      ...lines
        .filter(line => line.trim())
        .map(line => {
          const match = line.match(/^(\s*)/);
          return match?.[1]?.length ?? 0;
        })
    );

    // Remove common indentation
    return lines
      .map(line => line.slice(minIndent))
      .join('\n')
      .trim();
  }

  get code(): string {
    return this.#originalContent;
  }

  set code(value: string) {
    this.#originalContent = value;
    this.#render();
  }
}

customElements.define('ry-code', RyCode);
